import { useEffect, useState } from "react";
import api from "@/lib/api";
import { LEVEL_META, SESSION_STATUS, mondayOf, shortDate, dateLabel, waLink } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar } from "@/components/PhotoUpload";
import { SessionDialog } from "@/components/SessionDialog";
import { Loader2, ChevronLeft, ChevronRight, CalendarDays, Repeat, MessageCircle, Sparkles } from "lucide-react";

function SessionItem({ s, onOpen }) {
  const st = SESSION_STATUS[s.status];
  return (
    <button data-testid={`session-${s.kind}-${s.student_id}-${s.date}`} onClick={() => onOpen(s)}
      className={`w-full text-left p-2.5 rounded-xl transition-colors ${s.kind === "pengganti" ? "bg-violet-50 hover:bg-violet-100" : "bg-amber-50 hover:bg-amber-100"}`}>
      <div className="flex items-center gap-2">
        <Avatar src={s.photo} name={s.name} size="w-8 h-8" rounded="rounded-lg" text="text-xs" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-orange-600">{s.time} {s.kind === "pengganti" && <Repeat className="w-3 h-3 inline text-violet-500" />}</div>
          <div className="text-sm font-medium text-slate-800 truncate">{s.name}</div>
        </div>
      </div>
      <div className="flex items-center gap-1 mt-1.5 flex-wrap">
        <Badge variant="outline" className={`text-[10px] rounded-full ${LEVEL_META[s.level]?.cls}`}>{s.level}</Badge>
        <Badge data-testid={`session-status-${s.student_id}-${s.date}`} variant="outline" className={`text-[10px] rounded-full ${st?.cls}`}>{st?.label}</Badge>
      </div>
    </button>
  );
}

export default function Schedule() {
  const [start, setStart] = useState(mondayOf());
  const [data, setData] = useState(null);
  const [reschedules, setReschedules] = useState([]);
  const [students, setStudents] = useState([]);
  const [sel, setSel] = useState(null);

  const load = () => {
    api.get("/sessions/week", { params: { start } }).then((r) => setData(r.data));
    api.get("/reschedules").then((r) => setReschedules(r.data));
    api.get("/students", { params: { status: "aktif" } }).then((r) => setStudents(r.data));
  };
  useEffect(load, [start]);

  const shift = (n) => { const d = new Date(start); d.setDate(d.getDate() + n * 7); setStart(mondayOf(d)); };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;
  const todayCol = data.days.find((d) => d.is_today);

  return (
    <div className="space-y-6 animate-float-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Jadwal & Pengganti</h1>
          <p className="text-slate-500 mt-1">Klik sesi untuk tandai kehadiran, isi jurnal, atau atur pengganti</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" data-testid="week-prev-btn" onClick={() => shift(-1)} className="rounded-xl"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" data-testid="week-today-btn" onClick={() => setStart(mondayOf())} className="rounded-xl">Minggu Ini</Button>
          <Button variant="outline" size="icon" data-testid="week-next-btn" onClick={() => shift(1)} className="rounded-xl"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {todayCol && (
        <Card className="p-5 rounded-2xl border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-amber-50" data-testid="today-card">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-orange-500" />
            <h2 className="font-display text-xl font-bold text-slate-900">Jadwal Hari Ini · {todayCol.day}, {shortDate(todayCol.date)}</h2>
            <span className="ml-auto text-sm text-slate-500">{todayCol.sessions.length} sesi</span>
          </div>
          {todayCol.sessions.length === 0 ? <p className="text-sm text-slate-400">Tidak ada kelas hari ini 🌙</p> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {todayCol.sessions.map((s, i) => <SessionItem key={i} s={s} onOpen={setSel} />)}
            </div>
          )}
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        {data.days.map((d) => (
          <Card key={d.date} data-testid={`day-col-${d.date}`} className={`p-3 rounded-2xl ${d.is_today ? "border-2 border-orange-400 bg-orange-50/60 shadow-md shadow-orange-100" : "border-amber-100"}`}>
            <div className="flex items-start gap-2 mb-3">
              <CalendarDays className={`w-4 h-4 mt-0.5 ${d.is_today ? "text-orange-600" : "text-slate-400"}`} />
              <div className="min-w-0">
                <h3 className={`font-display font-bold ${d.is_today ? "text-orange-700" : "text-slate-900"}`}>{d.day}{d.is_today && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500 text-white align-middle">Hari ini</span>}</h3>
                <div className="text-[11px] text-slate-500">{shortDate(d.date)}</div>
              </div>
              <span className="ml-auto text-xs text-slate-400">{d.sessions.length}</span>
            </div>
            <div className="space-y-2">
              {d.sessions.length === 0 ? <p className="text-xs text-slate-300 py-2">Libur</p> :
                d.sessions.map((s, i) => <SessionItem key={i} s={s} onOpen={setSel} />)}
            </div>
          </Card>
        ))}
      </div>

      <div>
        <h2 className="font-display text-2xl font-bold text-slate-900 mb-3">Daftar Jadwal Pengganti</h2>
        {reschedules.length === 0 ? (
          <Card className="p-8 text-center rounded-2xl border-amber-100 text-slate-400">Belum ada jadwal pengganti. Klik sesi yang tidak dihadiri untuk mengatur pengganti.</Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {reschedules.map((r) => {
              const st = students.find((s) => s.id === r.student_id);
              return (
                <Card key={r.id} data-testid={`reschedule-${r.id}`} className="p-4 rounded-2xl border-violet-100 bg-violet-50/40">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold text-slate-900">{r.student_name}</div>
                      <div className="text-sm text-slate-500 mt-1">
                        <span className="line-through text-rose-400">{dateLabel(r.original_date)}</span>{" → "}
                        <span className="text-emerald-600 font-semibold">{dateLabel(r.new_date)} {r.new_time}</span>
                      </div>
                      {r.reason && <div className="text-xs text-slate-400 mt-1">Alasan: {r.reason}</div>}
                    </div>
                    <div className="flex gap-1">
                      {st && <Button size="sm" variant="ghost" onClick={() => window.open(waLink(st.wa, `Assalamu'alaikum, jadwal les ananda ${r.student_name} diganti menjadi ${dateLabel(r.new_date)} pukul ${r.new_time}. Terima kasih 🙏`), "_blank")} className="rounded-lg text-emerald-600 h-8 px-2"><MessageCircle className="w-4 h-4" /></Button>}
                      <Button size="sm" variant="outline" data-testid={`manage-reschedule-${r.id}`} onClick={() => setStart(mondayOf(new Date(r.original_date)))} className="rounded-lg h-8 text-xs">Lihat</Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <SessionDialog session={sel} onClose={() => setSel(null)} onChanged={load} />
    </div>
  );
}
